import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Eye, Filter, Calendar, User, Printer, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const SolicitudesBecas = () => {
  // Usar siempre window.location.origin para que funcione con cualquier dominio
  const API = `${window.location.origin}/api`;
  
  const [loading, setLoading] = useState(true);
  const [solicitudes, setSolicitudes] = useState([]);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [solicitudSeleccionada, setSolicitudSeleccionada] = useState(null);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [accion, setAccion] = useState(null); // 'aprobar' o 'rechazar'
  const [fechaVencimiento, setFechaVencimiento] = useState('');
  const [comentarios, setComentarios] = useState('');
  const [procesando, setProcesando] = useState(false);

  useEffect(() => {
    cargarSolicitudes();
  }, [filtroEstado]);

  const cargarSolicitudes = async () => {
    try {
      setLoading(true);
      
      let url = `${API}/becas/solicitudes`;
      if (filtroEstado) {
        url += `?estado=${filtroEstado}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSolicitudes(data);
      } else {
        toast.error('Error al cargar solicitudes');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar solicitudes');
    } finally {
      setLoading(false);
    }
  };

  const abrirModal = (solicitud, tipoAccion) => {
    setSolicitudSeleccionada(solicitud);
    setAccion(tipoAccion);
    setComentarios('');
    
    // Sugerir fecha de vencimiento 1 año después
    if (tipoAccion === 'aprobar') {
      const fechaSugerida = new Date();
      fechaSugerida.setFullYear(fechaSugerida.getFullYear() + 1);
      setFechaVencimiento(fechaSugerida.toISOString().split('T')[0]);
    }
    
    setMostrarModal(true);
  };

  const cerrarModal = () => {
    setMostrarModal(false);
    setSolicitudSeleccionada(null);
    setAccion(null);
    setFechaVencimiento('');
    setComentarios('');
  };

  const aprobarBeca = async () => {
    if (!fechaVencimiento) {
      toast.error('Por favor selecciona una fecha de vencimiento');
      return;
    }

    try {
      setProcesando(true);

      const response = await fetch(`${API}/becas/solicitudes/${solicitudSeleccionada.id}/aprobar`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          fecha_vencimiento: fechaVencimiento + 'T23:59:59Z',
          comentarios_admin: comentarios
        })
      });

      if (response.ok) {
        toast.success('Beca aprobada y email enviado al estudiante');
        cerrarModal();
        await cargarSolicitudes();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al aprobar beca');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al aprobar beca');
    } finally {
      setProcesando(false);
    }
  };

  const rechazarBeca = async () => {
    if (!comentarios.trim()) {
      toast.error('Por favor proporciona una razón para el rechazo');
      return;
    }

    try {
      setProcesando(true);

      const response = await fetch(`${API}/becas/solicitudes/${solicitudSeleccionada.id}/rechazar`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          comentarios_admin: comentarios
        })
      });

      if (response.ok) {
        toast.success('Beca rechazada y email enviado al estudiante');
        cerrarModal();
        await cargarSolicitudes();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al rechazar beca');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al rechazar beca');
    } finally {
      setProcesando(false);
    }
  };

  const eliminarSolicitud = async (solicitudId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta solicitud de beca?\n\nEsta acción no se puede deshacer.')) {
      return;
    }

    try {
      setProcesando(true);

      const response = await fetch(`${API}/becas/solicitudes/${solicitudId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        toast.success('Solicitud eliminada exitosamente');
        await cargarSolicitudes();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al eliminar solicitud');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al eliminar solicitud');
    } finally {
      setProcesando(false);
    }
  };


  const limpiarTodasLasSolicitudes = async () => {
    if (!window.confirm('⚠️ ATENCIÓN: Esto eliminará TODAS las solicitudes de becas, incluyendo las corruptas que no se ven.\n\n¿Estás SEGURO de continuar?')) {
      return;
    }

    try {
      setProcesando(true);

      const response = await fetch(`${API}/becas/solicitudes/limpiar-todas`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`${data.deleted_count} solicitudes eliminadas (incluyendo corruptas)`);
        await cargarSolicitudes();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al limpiar solicitudes');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al limpiar solicitudes');
    } finally {
      setProcesando(false);
    }
  };


  const eliminarSolicitudFantasma = async () => {
    const mongodbId = '68f76ef14c00e7be85f61940'; // ID conocido de la solicitud fantasma
    
    if (!window.confirm('¿Eliminar la solicitud fantasma detectada?\n\nID MongoDB: ' + mongodbId)) {
      return;
    }

    try {
      setProcesando(true);

      const response = await fetch(`${API}/becas/solicitudes/eliminar-por-mongodb-id/${mongodbId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Solicitud fantasma eliminada exitosamente');
        await cargarSolicitudes();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al eliminar solicitud fantasma');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al eliminar solicitud fantasma');
    } finally {
      setProcesando(false);
    }
  };

  const verDebugSolicitudes = async () => {
    try {
      const response = await fetch(`${API}/becas/solicitudes/debug`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('🔍 DEBUG SOLICITUDES:', data);
        
        // Mostrar información en alert detallado
        let mensaje = `📊 INFORMACIÓN DE DEBUG\n\n`;
        mensaje += `Total solicitudes en BD: ${data.total}\n\n`;
        
        if (data.total > 0) {
          mensaje += `SOLICITUDES ENCONTRADAS:\n`;
          mensaje += `========================\n\n`;
          data.solicitudes.forEach((s, index) => {
            mensaje += `Solicitud ${index + 1}:\n`;
            mensaje += `ID: ${s.id}\n`;
            mensaje += `MongoDB _id: ${s._id}\n`;
            mensaje += `Estudiante: ${s.estudiante}\n`;
            mensaje += `Estado: ${s.estado}\n`;
            mensaje += `Tiene apellido: ${s.tiene_apellido ? 'SÍ' : 'NO ❌'}\n`;
            mensaje += `Tiene semestre_id: ${s.tiene_semestre_id ? 'SÍ' : 'NO ❌'}\n`;
            mensaje += `Tiene formulario_id: ${s.tiene_formulario_id ? 'SÍ' : 'NO ❌'}\n`;
            mensaje += `Tipo respuestas: ${s.tipo_respuestas}\n`;
            mensaje += `\n`;
          });
        } else {
          mensaje += `✅ No hay solicitudes en la base de datos`;
        }
        
        alert(mensaje);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al obtener debug');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al obtener información de debug');
    }
  };



  const getEstadoBadge = (estado) => {
    const badges = {
      pendiente: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pendiente', icon: '⏳' },
      aprobada: { bg: 'bg-green-100', text: 'text-green-800', label: 'Aprobada', icon: '✅' },
      rechazada: { bg: 'bg-red-100', text: 'text-red-800', label: 'Rechazada', icon: '❌' }
    };
    
    const badge = badges[estado] || badges.pendiente;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${badge.bg} ${badge.text}`}>
        {badge.icon} {badge.label}
      </span>
    );
  };

  const imprimirSolicitud = () => {
    // Crear ventana de impresión con el contenido del modal
    const printWindow = window.open('', '', 'width=800,height=600');
    
    const contenido = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Solicitud de Beca - ${solicitudSeleccionada.estudiante_nombre}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #1f2937; border-bottom: 2px solid #3b82f6; padding-bottom: 10px; }
          h2 { color: #374151; margin-top: 20px; }
          .info-box { background: #f3f4f6; padding: 15px; border-radius: 8px; margin: 15px 0; }
          .pregunta { margin: 15px 0; padding: 10px; border-left: 3px solid #3b82f6; }
          .pregunta-titulo { font-weight: bold; color: #1f2937; margin-bottom: 5px; }
          .respuesta { color: #374151; margin-left: 10px; }
          .badge { display: inline-block; padding: 4px 12px; border-radius: 12px; font-size: 12px; font-weight: 600; }
          .badge-pendiente { background: #fef3c7; color: #92400e; }
          .badge-aprobada { background: #d1fae5; color: #065f46; }
          .badge-rechazada { background: #fee2e2; color: #991b1b; }
          @media print {
            body { margin: 0; padding: 15px; }
          }
        </style>
      </head>
      <body>
        <h1>📋 Solicitud de Beca</h1>
        
        <div class="info-box">
          <h2>Información del Estudiante</h2>
          <p><strong>Nombre:</strong> ${solicitudSeleccionada.estudiante_nombre} ${solicitudSeleccionada.estudiante_apellido}</p>
          <p><strong>Email:</strong> ${solicitudSeleccionada.estudiante_email}</p>
          <p><strong>Semestre:</strong> ${solicitudSeleccionada.semestre_nombre}</p>
          <p><strong>Fecha de solicitud:</strong> ${new Date(solicitudSeleccionada.fecha_solicitud).toLocaleDateString()}</p>
          <p><strong>Estado:</strong> <span class="badge badge-${solicitudSeleccionada.estado}">${solicitudSeleccionada.estado.toUpperCase()}</span></p>
        </div>

        <h2>Respuestas del Formulario</h2>
        ${Object.entries(solicitudSeleccionada.respuestas).map(([preguntaId, respuesta], index) => {
          const enr = (solicitudSeleccionada.respuestas_enriquecidas || {})[preguntaId] || {};
          const tituloPregunta = enr.pregunta_texto || `Pregunta ${index + 1}`;
          const valorMostrar = Array.isArray(respuesta) ? respuesta.join(', ') : (respuesta ?? '');
          return `
          <div class="pregunta">
            <div class="pregunta-titulo">${tituloPregunta}</div>
            <div class="respuesta">${valorMostrar}</div>
          </div>
        `;
        }).join('')}

        ${solicitudSeleccionada.comentarios_admin ? `
          <div class="info-box">
            <h2>Comentarios del Administrador</h2>
            <p>${solicitudSeleccionada.comentarios_admin}</p>
          </div>
        ` : ''}

        ${solicitudSeleccionada.fecha_aprobacion ? `
          <div class="info-box">
            <h2>Información de Aprobación</h2>
            <p><strong>Aprobado por:</strong> ${solicitudSeleccionada.aprobado_por_nombre}</p>
            <p><strong>Fecha de aprobación:</strong> ${new Date(solicitudSeleccionada.fecha_aprobacion).toLocaleDateString()}</p>
            ${solicitudSeleccionada.fecha_vencimiento ? `<p><strong>Válida hasta:</strong> ${new Date(solicitudSeleccionada.fecha_vencimiento).toLocaleDateString()}</p>` : ''}
          </div>
        ` : ''}

        <script>
          window.onload = function() {
            window.print();
            // Opcional: cerrar ventana después de imprimir
            // window.onafterprint = function() { window.close(); }
          }
        </script>
      </body>
      </html>
    `;
    
    printWindow.document.write(contenido);
    printWindow.document.close();
  };

  const getEstadisticas = () => {
    return {
      total: solicitudes.length,
      pendientes: solicitudes.filter(s => s.estado === 'pendiente').length,
      aprobadas: solicitudes.filter(s => s.estado === 'aprobada').length,
      rechazadas: solicitudes.filter(s => s.estado === 'rechazada').length
    };
  };

  const stats = getEstadisticas();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando solicitudes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            📋 Gestión de Solicitudes de Becas
          </h1>
          <p className="text-gray-600 mt-2">Revisa, aprueba o rechaza solicitudes de becas de estudiantes</p>
        </div>
        {/* Botones de administración - DESHABILITADOS (descomentar si se necesitan) */}
        {/* 
        <div className="flex gap-2">
          <button
            onClick={verDebugSolicitudes}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center gap-2"
            title="Ver información de debug de solicitudes"
          >
            🔍 Debug
          </button>
          <button
            onClick={eliminarSolicitudFantasma}
            disabled={procesando}
            className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg font-semibold disabled:opacity-50 flex items-center gap-2"
            title="Eliminar solicitud fantasma específica"
          >
            👻 Eliminar Fantasma
          </button>
          <button
            onClick={limpiarTodasLasSolicitudes}
            disabled={procesando}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold disabled:opacity-50 flex items-center gap-2"
            title="Eliminar TODAS las solicitudes (incluyendo corruptas)"
          >
            <Trash2 className="h-4 w-4" />
            Limpiar Todas
          </button>
        </div>
        */}
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <div className="bg-blue-100 rounded-full p-3">
              <User className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pendientes</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.pendientes}</p>
            </div>
            <div className="bg-yellow-100 rounded-full p-3">
              <Calendar className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Aprobadas</p>
              <p className="text-2xl font-bold text-green-600">{stats.aprobadas}</p>
            </div>
            <div className="bg-green-100 rounded-full p-3">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Rechazadas</p>
              <p className="text-2xl font-bold text-red-600">{stats.rechazadas}</p>
            </div>
            <div className="bg-red-100 rounded-full p-3">
              <XCircle className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="flex items-center gap-4">
          <Filter className="h-5 w-5 text-gray-600" />
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Todas las solicitudes</option>
            <option value="pendiente">Solo Pendientes</option>
            <option value="aprobada">Solo Aprobadas</option>
            <option value="rechazada">Solo Rechazadas</option>
          </select>
        </div>
      </div>

      {/* Lista de Solicitudes */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {solicitudes.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p>No hay solicitudes de becas{filtroEstado && ` con estado "${filtroEstado}"`}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estudiante</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Semestre</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Solicitud</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {solicitudes.map(solicitud => (
                  <tr key={solicitud.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-semibold text-gray-900">
                          {solicitud.estudiante_nombre} {solicitud.estudiante_apellido}
                        </p>
                        <p className="text-sm text-gray-600">{solicitud.estudiante_email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-gray-900">{solicitud.semestre_nombre}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-gray-900">
                        {new Date(solicitud.fecha_solicitud).toLocaleDateString()}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      {getEstadoBadge(solicitud.estado)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => {
                            setSolicitudSeleccionada(solicitud);
                            setMostrarModal(true);
                          }}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                          title="Ver detalles"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {solicitud.estado === 'pendiente' && (
                          <>
                            <button
                              onClick={() => abrirModal(solicitud, 'aprobar')}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                              title="Aprobar"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => abrirModal(solicitud, 'rechazar')}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                              title="Rechazar"
                            >
                              <XCircle className="h-4 w-4" />
                            </button>
                          </>
                        )}
                        {/* Botón eliminar disponible para cualquier estado */}
                        <button
                          onClick={() => eliminarSolicitud(solicitud.id)}
                          disabled={procesando}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                          title="Eliminar solicitud"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de Detalle/Acción */}
      {mostrarModal && solicitudSeleccionada && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    Solicitud de {solicitudSeleccionada.estudiante_nombre}
                  </h2>
                  <p className="text-gray-600">{solicitudSeleccionada.semestre_nombre}</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={imprimirSolicitud}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                    title="Imprimir solicitud"
                  >
                    <Printer className="h-5 w-5" />
                  </button>
                  <button
                    onClick={cerrarModal}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircle className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Info del Estudiante */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">Información del Estudiante</h3>
                <p className="text-sm text-gray-700">
                  <strong>Email:</strong> {solicitudSeleccionada.estudiante_email}
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Fecha de solicitud:</strong>{' '}
                  {new Date(solicitudSeleccionada.fecha_solicitud).toLocaleDateString()}
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Estado:</strong> {getEstadoBadge(solicitudSeleccionada.estado)}
                </p>
              </div>

              {/* Respuestas */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 mb-3">Respuestas del Formulario</h3>
                <div className="space-y-3">
                  {Object.entries(solicitudSeleccionada.respuestas).map(([preguntaId, respuesta], index) => {
                    const enr = (solicitudSeleccionada.respuestas_enriquecidas || {})[preguntaId] || {};
                    const titulo = enr.pregunta_texto || `Pregunta ${index + 1}`;
                    const valor = Array.isArray(respuesta) ? respuesta.join(', ') : (respuesta ?? '');
                    return (
                      <div key={preguntaId} className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm font-medium text-gray-700 mb-1">{titulo}</p>
                        <p className="text-gray-900 whitespace-pre-wrap">{valor}</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Acciones */}
              {accion === 'aprobar' && (
                <div className="space-y-4 bg-green-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-green-900">Aprobar Beca</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fecha de Vencimiento *
                    </label>
                    <input
                      type="date"
                      value={fechaVencimiento}
                      onChange={(e) => setFechaVencimiento(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                    <p className="text-xs text-gray-600 mt-1">
                      Hasta cuándo será válida esta beca
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Comentarios (opcional)
                    </label>
                    <textarea
                      value={comentarios}
                      onChange={(e) => setComentarios(e.target.value)}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder="Mensaje para el estudiante..."
                    />
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={aprobarBeca}
                      disabled={procesando}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                    >
                      {procesando ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Procesando...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="h-4 w-4" />
                          Confirmar Aprobación
                        </>
                      )}
                    </button>
                    <button
                      onClick={cerrarModal}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}

              {accion === 'rechazar' && (
                <div className="space-y-4 bg-red-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-red-900">Rechazar Beca</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Razón del Rechazo *
                    </label>
                    <textarea
                      value={comentarios}
                      onChange={(e) => setComentarios(e.target.value)}
                      rows={4}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                      placeholder="Explica por qué se rechaza la solicitud..."
                    />
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={rechazarBeca}
                      disabled={procesando}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                    >
                      {procesando ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Procesando...
                        </>
                      ) : (
                        <>
                          <XCircle className="h-4 w-4" />
                          Confirmar Rechazo
                        </>
                      )}
                    </button>
                    <button
                      onClick={cerrarModal}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}

              {!accion && (
                <div className="flex justify-end gap-2">
                  {solicitudSeleccionada.estado === 'pendiente' && (
                    <>
                      <button
                        onClick={() => setAccion('aprobar')}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                      >
                        <CheckCircle className="h-4 w-4" />
                        Aprobar
                      </button>
                      <button
                        onClick={() => setAccion('rechazar')}
                        className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                      >
                        <XCircle className="h-4 w-4" />
                        Rechazar
                      </button>
                    </>
                  )}
                  <button
                    onClick={cerrarModal}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                  >
                    Cerrar
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SolicitudesBecas;
